import { useState } from 'react';
import { GraduationCap, School, Users, BookOpen, Check, ArrowRight, ArrowUpRight, Shield, Clock, Mail, Building, ChevronDown, Target, Calendar } from 'lucide-react';
import SEO from '@/components/SEO';
import { site } from '@/data/content';

const whySupport = [
  { icon: School, title: 'Built for schools', text: 'Admissions, parent queries, student help, and faculty coordination should not require multiple tools.' },
  { icon: Users, title: 'For every department', text: 'Admissions, IT, finance, and student affairs can share one inbox while keeping their own workflows.' },
  { icon: Clock, title: 'Budget-friendly annual plans', text: 'Get 39% off annual subscriptions when you start with 5 agents or more, making it easier to scale across campus.' },
];

const offering = [
  { icon: Target, title: '39% annual discount', text: 'Schools and institutions get 39% off annual subscriptions for 5 or more agents.' },
  { icon: BookOpen, title: 'Public help centers', text: 'Publish FAQs for students, parents, and staff so common questions are answered around the clock.' },
  { icon: Shield, title: 'Role-based access', text: 'Control which staff can see admissions, finance, or IT conversations.' },
  { icon: Calendar, title: 'Term-friendly billing', text: 'Annual plans align with the academic year and simplify budget planning.' },
];

const eligibility = [
  'Accredited school, college, university, or training institution',
  'Public or private educational institution',
  'Minimum of 5 agents on the annual plan',
  'Use for student, parent, or staff support workflows',
];

const faqs = [
  { q: 'How much is the education discount?', a: 'Schools and institutions receive a 39% discount on annual subscriptions when they start with 5 or more agents. The discount applies for the annual billing cycle and renewals at the same rate.' },
  { q: 'Do we need to be a university to qualify?', a: 'No. K-12 schools, colleges, training centers, and accredited online learning institutions are all eligible. We require proof of accreditation or institutional status.' },
  { q: 'Can students or teachers use ZuriHub for free?', a: 'The 39% discount applies to annual institutional plans of 5+ agents. Separate classroom or research use cases may be considered on request.' },
  { q: 'What channels are most useful for education teams?', a: 'WhatsApp, live chat, email, and Instagram are commonly used for admissions, parent communication, and student support.' },
];

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'ZuriHub for Education',
    description: '39% discount on annual subscriptions for 5+ agents at schools, colleges, and educational institutions. Omnichannel support and AI help desk for education.',
    url: `${site.baseUrl}/zurihub-for-education`,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${site.baseUrl}/zurihub-for-education` },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  },
];

export default function ZuriHubEducation() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    institution: '',
    country: '',
    website: '',
    agents: '',
    why: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = `Education program application from ${form.institution || form.name}`;
    const body = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Institution: ${form.institution}`,
      `Country: ${form.country}`,
      `Website: ${form.website || 'Not provided'}`,
      `Agent seats: ${form.agents || 'Not provided'}`,
      '',
      'Use case:',
      form.why || 'No details provided',
    ].join('\n');
    window.location.href = `mailto:education@getzurihub.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSubmitted(true);
  };

  return (
    <>
      <SEO
        title="ZuriHub for Education"
        description="Schools, colleges, and institutions get 39% off annual subscriptions for 5+ agents. Omnichannel help desk, AI, and knowledge base for education."
        path="/zurihub-for-education"
        jsonLd={jsonLd}
      >
        <meta
          name="keywords"
          content="education help desk, school support software, student help desk, university support, admissions support, parent communication, education discount"
        />
      </SEO>

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-28 pb-20 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-4 py-1.5 text-sm font-medium text-slate-300 backdrop-blur">
                <GraduationCap className="h-4 w-4" /> For Education
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                ZuriHub for <span className="gradient-text">Education</span>
              </h1>
              <p className="mx-auto mt-5 max-w-2xl text-lg text-slate-300">
                Schools, colleges, and institutions get 39% off annual subscriptions with 5 or more agents. Support students, parents, and staff from one shared inbox.
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <a
                  href="#apply"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-7 py-3.5 text-base font-bold text-white transition hover:bg-brand-700"
                >
                  Apply now <ChevronDown className="h-4 w-4" />
                </a>
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-7 py-3.5 text-base font-semibold text-white transition hover:bg-slate-800"
                >
                  Try free <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-900/60 p-2 backdrop-blur">
              <img
                src="https://media.getzurihub.com/Jayrah%20Assets/ITSM.avif"
                alt="ZuriHub for Education"
                className="rounded-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50" aria-labelledby="why-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 id="why-heading" className="section-title">Support built for campuses</h2>
            <p className="section-subtitle mx-auto mt-3">Admissions, help desk, and campus-wide communication in one place.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-3">
            {whySupport.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="rounded-3xl border border-slate-100 bg-white p-7 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-100 text-brand-700">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20" aria-labelledby="offering-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            <div>
              <h2 id="offering-heading" className="section-title">What education teams get</h2>
              <p className="mt-3 text-slate-600">A complete support platform at a discount, designed for the academic calendar.</p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {offering.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.title} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
                      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="font-bold text-slate-900">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-600">{item.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-xl shadow-slate-200/50">
              <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Check className="h-5 w-5 text-green-600" /> Eligibility
              </h3>
              <ul className="mt-5 space-y-3">
                {eligibility.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-700">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 rounded-2xl bg-slate-900 p-5 text-white">
                <p className="text-sm text-slate-300">
                  <span className="font-bold text-white">Example:</span> A school with 10 agents on Pro saves over $585/year with the 39% annual education discount.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="section-title text-center">Education program FAQs</h2>
          <p className="section-subtitle mx-auto mt-4">Common questions about the discount and approval process.</p>
          <div className="mt-10 space-y-4">
            {faqs.map((faq) => (
              <details key={faq.q} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm group">
                <summary className="flex cursor-pointer list-none items-center justify-between text-left font-semibold text-slate-900">
                  {faq.q}
                  <span className="ml-4 transition group-open:rotate-180">&#9662;</span>
                </summary>
                <p className="mt-3 text-slate-600">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section id="apply" className="py-20" aria-label="Apply for education discount">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-start gap-10 lg:grid-cols-2">
            <div>
              <h2 className="section-title">Apply for the education discount</h2>
              <p className="mt-3 text-slate-600">Tell us about your institution and we will review your application within 2-3 business days.</p>
            </div>

            <div>
              {submitted ? (
                <div className="rounded-3xl border border-green-200 bg-green-50 p-8 text-center shadow-sm">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600">
                    <Check className="h-7 w-7" />
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-slate-900">Application ready</h3>
                  <p className="mt-2 text-sm text-slate-600">Your email client is open. Send the message to education@getzurihub.com and we will get back to you within 2-3 business days.</p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setForm({ name: '', email: '', institution: '', country: '', website: '', agents: '', why: '' });
                    }}
                    className="mt-5 rounded-full bg-brand-600 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-700"
                  >
                    Apply again
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/50">
                  <h3 className="text-xl font-bold text-slate-900">Start your application</h3>
                  <p className="mt-1 text-sm text-slate-600">Fill in your details and we will email you the next steps.</p>
                  <div className="mt-5 space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="name" className="block text-xs font-semibold text-slate-700">Full name</label>
                        <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20">
                          <Users className="h-4 w-4 text-slate-400" />
                          <input id="name" type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent text-sm outline-none text-slate-900 placeholder:text-slate-400" placeholder="Jane Doe" />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="institution" className="block text-xs font-semibold text-slate-700">Institution</label>
                        <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20">
                          <Building className="h-4 w-4 text-slate-400" />
                          <input id="institution" type="text" required value={form.institution} onChange={(e) => setForm({ ...form, institution: e.target.value })} className="w-full bg-transparent text-sm outline-none text-slate-900 placeholder:text-slate-400" placeholder="Bright High School" />
                        </div>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs font-semibold text-slate-700">Work email</label>
                      <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20">
                        <Mail className="h-4 w-4 text-slate-400" />
                        <input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-transparent text-sm outline-none text-slate-900 placeholder:text-slate-400" placeholder="jane@bright.ac.ug" />
                      </div>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="country" className="block text-xs font-semibold text-slate-700">Country</label>
                        <input id="country" type="text" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" placeholder="Uganda" />
                      </div>
                      <div>
                        <label htmlFor="agents" className="block text-xs font-semibold text-slate-700">Agent seats needed</label>
                        <input id="agents" type="number" min={5} value={form.agents} onChange={(e) => setForm({ ...form, agents: e.target.value })} className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" placeholder="5+" />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="website" className="block text-xs font-semibold text-slate-700">Website (optional)</label>
                      <input id="website" type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" placeholder="https://bright.ac.ug" />
                    </div>
                    <div>
                      <label htmlFor="why" className="block text-xs font-semibold text-slate-700">How will ZuriHub help your institution?</label>
                      <textarea id="why" value={form.why} onChange={(e) => setForm({ ...form, why: e.target.value })} rows={4} className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20" placeholder="Admissions, student support, parent communication..." />
                    </div>
                    <button type="submit" className="w-full rounded-full bg-brand-600 py-3 text-sm font-semibold text-white hover:bg-brand-700">
                      Request education discount <ArrowRight className="inline h-4 w-4 ml-2" />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
